import { useEffect, useState } from "react";
import type { PolicyPageConfig, WorkplacePolicy } from "../scenario/types";
import { ackPolicies } from "../lib/hostConfig";
import { tx, txr } from "../lib/tx";

type Host = { __CF_SIM__?: { token?: string } };
/** Library items from the host carry versionId; built-in defaults do not and stay local. */
type HostPolicy = WorkplacePolicy & { versionId?: string };

export function PolicyLibrary({
  config,
  onContinue,
}: {
  config: PolicyPageConfig;
  onContinue: () => void;
}) {
  const [policies, setPolicies] = useState<HostPolicy[]>(config.policies);
  const [openId, setOpenId] = useState<string | null>(null);
  const [acked, setAcked] = useState<Set<string>>(new Set());

  useEffect(() => {
    document.querySelector(".pol-body")?.scrollTo(0, 0);
  }, [openId]);

  useEffect(() => {
    const token = (window as unknown as Host).__CF_SIM__?.token;
    if (!token) return;
    let cancelled = false;
    fetch(`/sim/workplace-policies/${encodeURIComponent(token)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { policies?: HostPolicy[] } | null) => {
        if (!cancelled && data?.policies?.length) setPolicies(data.policies);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const open = policies.find((p) => p.id === openId) ?? null;
  const currentAcked = open ? acked.has(open.id) : true;

  const acknowledge = (policy: HostPolicy) => {
    if (policy.versionId && !acked.has(policy.id)) ackPolicies([policy.versionId]);
    setAcked((prev) => new Set(prev).add(policy.id));
  };

  const goNext = () => {
    if (open && !acked.has(open.id)) return;
    const start = open ? policies.findIndex((p) => p.id === open.id) + 1 : 0;
    const rest = [...policies.slice(start), ...policies.slice(0, start)];
    const next = rest.find((p) => !acked.has(p.id));
    if (next) {
      setOpenId(next.id);
      return;
    }
    onContinue();
  };

  return (
    <div className="pol">
      <div className="pol-window">
        <div className="pol-chrome">
          <span className="pol-dot" />
          <span className="pol-dot" />
          <span className="pol-dot" />
          <span className="pol-url">{config.siteUrl ?? "policies.company.internal"}</span>
        </div>
        <div className="pol-body">
          {open ? (
            <article className="pol-doc">
              <button type="button" className="pol-back" onClick={() => setOpenId(null)}>
                {tx("All policies")}
              </button>
              <h2>{open.title}</h2>
              {open.summary && <p className="pol-summary">{open.summary}</p>}
              {open.body.split(/\n\n+/).map((para) => (
                <p key={para.slice(0, 40)}>{para}</p>
              ))}
              {open.documentUrl && (
                <p>
                  <a href={open.documentUrl} target="_blank" rel="noreferrer">
                    {tx("Open the full document")}
                  </a>
                </p>
              )}
            </article>
          ) : (
            <div className="pol-home">
              <h2>{config.companyName ?? tx("Company policies")}</h2>
              <p>{config.intro ?? tx("These policies are issued by your company. Open each one, read it, and acknowledge it.")}</p>
              <ul className="pol-list">
                {policies.map((policy) => (
                  <li key={policy.id}>
                    <button type="button" className="pol-link" onClick={() => setOpenId(policy.id)}>
                      <strong>{policy.title}</strong>
                      <span>{policy.summary}</span>
                      {acked.has(policy.id) && <em>{tx("Acknowledged")}</em>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <footer className="pol-foot">
          <span className="pol-progress">
            {txr("{size} of {length} acknowledged", { size: acked.size, length: policies.length })}
          </span>
          {open && (
            <button
              type="button"
              className="btn pol-ack"
              onClick={() => acknowledge(open)}
            >
              {acked.has(open.id) ? tx("Acknowledged") : tx("I have read this and will apply it")}
            </button>
          )}
          <button type="button" className="btn pol-continue" disabled={!currentAcked} onClick={goNext}>
            {tx("Continue")}
          </button>
        </footer>
      </div>
    </div>
  );
}
